import { Avatar, Button, IconButton } from "@heathmont/moon-core-tw";
import { ArrowsTransfer, ControlsCloseSmall, GenericAlarmRound } from "@heathmont/moon-icons-tw";
import React, { useState } from "react";
import { getEligibleSwaps, proposeSwap } from "../../../../../../services/request.service";
import moment from "moment";

interface EligibleColleague {
  colleague: { _id: string; firstName: string; lastName: string; role: string };
  colleagueShift: { day: number; month: number; year: number; shiftType: string };
}

const DateCard = ({
  avatarImageUrl,
  avatarSize,
  scheduledBy,
  day,
  date,
  month,
  year,
  dayNumber,
  monthNumber,
  yearNumber,
  shift,
}: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState<EligibleColleague[]>([]);
  const [selected, setSelected] = useState<EligibleColleague | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const shiftType = shift?.toLowerCase?.().includes("night") ? "night" : "day";

  const handleOpenSwap = async () => {
    setIsOpen(true);
    setError("");
    setSuccess(false);
    setSelected(null);
    setEligible([]);
    setDebugInfo(null);
    if (!dayNumber || !monthNumber || !yearNumber) return;
    setLoading(true);
    const res: any = await getEligibleSwaps(dayNumber, monthNumber, yearNumber, shiftType);
    setLoading(false);
    if (res?.res) {
      setDebugInfo(res.res.debug);
      if (Array.isArray(res.res.data) && res.res.data.length > 0) setEligible(res.res.data);
    } else {
      setError("Could not load eligible colleagues.");
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setError("");
    setSuccess(false);
    setSelected(null);
    setDebugInfo(null);
  };

  const handleApply = async () => {
    if (!selected || !dayNumber) return;
    setSubmitting(true);
    setError("");
    const res: any = await proposeSwap({
      colleagueId: selected.colleague._id,
      requesterShift: { day: dayNumber, month: monthNumber, year: yearNumber, shiftType },
      colleagueShift: selected.colleagueShift,
    });
    setSubmitting(false);
    if (res?.res) {
      setSuccess(true);
      setTimeout(() => handleClose(), 1800);
    } else {
      setError(res?.err?.message || "Failed to send swap request.");
    }
  };

  const formatShift = (s: EligibleColleague) =>
    `${moment({ year: s.colleagueShift.year, month: s.colleagueShift.month - 1, day: s.colleagueShift.day }).format("DD MMM")} · ${s.colleagueShift.shiftType}`;

  return (
    <>
      <div className="flex items-center">
        <div className="text-center px-[14px] py-[7px] bg-slate-100 rounded-lg">
          <h3 className="text-moon-18 font-semibold">{day}</h3>
          <div className="text-moon-32 font-semibold leading-none">{date}</div>
        </div>
        <div className="flex-1 px-[22px]">
          <h3 className="text-moon-18 font-semibold mb-[8px]">
            {month} {year}
          </h3>
          <div className="flex">
            <span className="text-gray-400">Scheduled by </span>
            <div className="flex flex-1 ml-[7px]">
              <div className="flex flex-wrap items-center justify-around gap-2 w-full">
                <Avatar imageUrl={avatarImageUrl} size={avatarSize} className="rounded-full overflow-hidden" />
                <span className="flex-1 font-bold">
                  {typeof scheduledBy === "object" && scheduledBy !== null
                    ? `${scheduledBy.firstName || ""} ${scheduledBy.lastName || ""}`.trim() || "—"
                    : scheduledBy || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <Button variant="secondary" onClick={handleOpenSwap}>
          <i className="text-moon-18"><ArrowsTransfer /></i> Swap
        </Button>
      </div>

      {/* Inline swap panel — avoids nested Drawer crash */}
      {isOpen && (
        <div className="mt-4 border border-beerus rounded-[12px] overflow-hidden">
          <div className="flex justify-between items-center px-4 py-3 border-b border-beerus bg-goku">
            <h3 className="text-moon-16 font-bold">Apply Swap</h3>
            <IconButton variant="ghost" size="sm" onClick={handleClose}>
              <ControlsCloseSmall />
            </IconButton>
          </div>

          <div className="p-4">
            {/* Current shift summary */}
            <div className="flex items-center mb-4">
              <div className="text-center px-3 py-2 bg-slate-100 rounded-lg mr-3">
                <div className="text-moon-12 font-semibold text-trunks">{day}</div>
                <div className="text-moon-24 font-semibold leading-none">{date}</div>
              </div>
              <div>
                <p className="text-moon-12 text-trunks">Your shift to give away</p>
                <p className="text-moon-14 font-semibold capitalize">{shiftType} shift · {day} {date}</p>
              </div>
            </div>

            <hr className="border-beerus mb-4" />

            <h4 className="text-moon-14 font-semibold mb-2">Swap with</h4>

            {loading && (
              <p className="text-moon-14 text-trunks">Finding eligible colleagues…</p>
            )}

            {!loading && eligible.length === 0 && !error && (
              <p className="text-moon-14 text-trunks">
                No eligible colleagues found. Either everyone is already working that day or a swap would break scheduling rules.
              </p>
            )}

            {!loading && eligible.length > 0 && (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {eligible.map((item) => (
                  <button
                    key={item.colleague._id + item.colleagueShift.day}
                    onClick={() => setSelected(item)}
                    className={`w-full text-left rounded-[8px] border p-3 transition-colors ${
                      selected?.colleague._id === item.colleague._id
                        ? "border-hit bg-hit/10"
                        : "border-beerus hover:border-hit/40"
                    }`}
                  >
                    <p className="text-moon-14 font-semibold">
                      {item.colleague.firstName} {item.colleague.lastName}
                      <span className="text-trunks font-normal ml-1 text-moon-12 capitalize">· {item.colleague.role}</span>
                    </p>
                    <p className="text-moon-12 text-trunks mt-0.5">
                      You'd take their: <span className="font-semibold text-bulma">{formatShift(item)}</span>
                    </p>
                  </button>
                ))}
              </div>
            )}

            {debugInfo && eligible.length === 0 && (
              <div className="mt-3 p-3 bg-slate-50 rounded text-moon-11 text-trunks break-all">
                <p className="font-semibold text-bulma mb-1">Debug info:</p>
                {debugInfo.reason && <p>reason: {debugInfo.reason}</p>}
                {debugInfo.sectionEmployeeCount !== undefined && (
                  <p>section employees: {debugInfo.sectionEmployeeCount}</p>
                )}
                {debugInfo.sectionNameResolved && <p>section: {debugInfo.sectionNameResolved}</p>}
                {debugInfo.filterReasons?.map((r: string, i: number) => (
                  <p key={i}>• {r}</p>
                ))}
              </div>
            )}

            {error && <p className="mt-2 text-moon-13 text-red-500">{error}</p>}
            {success && (
              <p className="mt-2 text-moon-13 text-green-600 font-semibold">
                Swap request sent to {selected?.colleague.firstName}!
              </p>
            )}
          </div>

          <div className="px-4 pb-3">
            <div className="flex items-start gap-2 mb-3 text-moon-12 text-trunks">
              <i className="text-moon-16 mt-0.5"><GenericAlarmRound /></i>
              <span>The colleague must accept before the swap takes effect.</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={handleClose}>Discard</Button>
              <Button
                size="sm"
                className="bg-hit"
                onClick={handleApply}
                disabled={!selected || submitting || success}
              >
                {submitting ? "Sending…" : "Send Request"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DateCard;
